import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Plus, User, UserCircle, LogOut, Package, ChevronDown, Sun, Moon, Heart, Bell, ArrowLeftRight, Shield, MessageCircle, Gavel, Calendar } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, useTheme, useLanguage, useCurrency, useWebSocket, useIsland, useNotifications, API } from '../App';
import { safeClick } from '../utils/safeClick';
import SearchBar from './SearchBar';

// Scroll to top utility
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * NavbarDesktop - Two-row desktop navbar layout
 * Top Row: Logo + Events | Create CTA + Currency + Messages + Notifications + User Menu
 * Bottom Row: Full-width SearchBar component
 */
const NavbarDesktop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const { currentIsland, setCurrentIsland } = useIsland();
  const { unreadCount: wsUnreadMessages } = useWebSocket();
  const { unreadCount: unreadNotifications, refresh: refreshNotifications } = useNotifications();
  
  // Island options
  const ISLAND_OPTIONS = [
    { code: 'aruba', label: 'Aruba', flag: '🇦🇼' },
    { code: 'bonaire', label: 'Bonaire', flag: '🇧🇶' },
    { code: 'curacao', label: 'Curaçao', flag: '🇨🇼' },
  ];
  
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [guestMenuOpen, setGuestMenuOpen] = useState(false);
  const [userBidCount, setUserBidCount] = useState(0);
  const [userEventCount, setUserEventCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const userMenuRef = useRef(null);
  const guestMenuRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  // Check admin status
  useEffect(() => {
    if (user) {
      axios.get(`${API}/users/me/is-admin`).then(res => {
        setIsAdmin(res.data.is_admin || false);
      }).catch(() => setIsAdmin(false));
      
      // Fetch user bid count
      axios.get(`${API}/users/me/bids/count`).then(res => {
        setUserBidCount(res.data.count || 0);
      }).catch(() => {});
      
      // Fetch user event count
      axios.get(`${API}/users/me/events/count`).then(res => {
        setUserEventCount(res.data.count || 0);
      }).catch(() => {});
    }
  }, [user]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (guestMenuRef.current && !guestMenuRef.current.contains(event.target)) {
        setGuestMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <nav className="hidden md:block sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border/50 shadow-sm" data-testid="navbar-desktop">
      {/* Top Row - Logo, Events, Actions - Higher z-index than search row for dropdown stacking */}
      <div className="border-b border-border/30 relative z-[70]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 gap-4">
            {/* Left: Logo + Events */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link 
                to="/" 
                className="flex items-center group py-2" 
                data-testid="logo-link"
                onClick={(e) => {
                  if (location.pathname === '/') {
                    e.preventDefault();
                    scrollToTop();
                  }
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/apiogjtg_ICONYBS.png" 
                  alt="Your Buy Site" 
                  className="h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                />
              </Link>
              
              {/* Events Link */}
              <Link 
                to="/events" 
                className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive('/events') 
                    ? 'bg-red-600 text-white' 
                    : 'text-foreground hover:bg-red-600 hover:text-white'
                }`}
                data-testid="nav-events"
              >
                <Calendar className="w-4 h-4" />
                Events
              </Link>
            </div>

            {/* Right Section - Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              
              {/* Create Button - Compact */}
              <Link
                to={user ? "/create-listing" : "/register"}
                className="flex items-center justify-center gap-1.5 h-9 px-4 text-sm font-medium bg-primary hover:bg-primary/90 text-primary-foreground rounded-full transition-all shadow-sm"
                data-testid="nav-create-listing"
              >
                <Plus className="w-4 h-4" />
                <span>{t('create')}</span>
              </Link>
              
              {/* Theme Toggle */}
              <div 
                className="flex items-center bg-secondary/80 rounded-full p-0.5 gap-0.5"
                data-testid="nav-theme-toggle"
              >
                <button
                  onClick={() => theme !== 'light' && toggleTheme()}
                  className={`p-1.5 rounded-full transition-all ${
                    theme === 'light' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title="Light mode"
                >
                  <Sun className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => theme !== 'dark' && toggleTheme()}
                  className={`p-1.5 rounded-full transition-all ${
                    theme === 'dark' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title="Dark mode"
                >
                  <Moon className="w-3.5 h-3.5" />
                </button>
              </div>
              
              {/* Currency Toggle */}
              <div 
                className="flex items-center bg-secondary/80 rounded-full p-0.5 gap-0.5"
                data-testid="nav-currency-toggle"
              >
                <button
                  onClick={() => setCurrency('XCG')}
                  className={`px-2 py-1 text-xs font-semibold rounded-full transition-all ${
                    currency === 'XCG' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  XCG
                </button>
                <button
                  onClick={() => setCurrency('USD')}
                  className={`px-2 py-1 text-xs font-semibold rounded-full transition-all ${
                    currency === 'USD' 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  $
                </button>
              </div>
              
              {user ? (
                <>
                  {/* Messages + Notifications */}
                  <div className="flex items-center gap-1">
                    <Link
                      to="/messages"
                      className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-secondary transition-colors relative"
                      data-testid="nav-messages"
                      title="Messages"
                    >
                      <MessageCircle className="w-5 h-5" />
                      {wsUnreadMessages > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {wsUnreadMessages > 9 ? '9+' : wsUnreadMessages}
                        </span>
                      )}
                    </Link>
                    
                    <Link
                      to="/notifications"
                      className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-secondary transition-colors relative"
                      data-testid="nav-notifications"
                      title="Notifications"
                      onClick={() => refreshNotifications && refreshNotifications()}
                    >
                      <Bell className="w-5 h-5" />
                      {unreadNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {unreadNotifications > 9 ? '9+' : unreadNotifications}
                        </span>
                      )}
                    </Link>
                  </div>

                  {/* User Dropdown */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      {...safeClick(() => setUserMenuOpen(!userMenuOpen))}
                      className="flex items-center gap-1.5 h-9 pl-1 pr-2 rounded-full hover:bg-secondary transition-all duration-200 cursor-pointer touch-manipulation"
                      data-testid="user-menu-btn"
                    >
                      <div className="w-7 h-7 rounded-full overflow-hidden ring-1 ring-border/50 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-primary to-blue-600 text-white text-[10px] font-semibold">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          user.name?.charAt(0).toUpperCase()
                        )}
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {userMenuOpen && (
                      <div className="absolute right-0 mt-3 w-64 bg-card rounded-2xl shadow-2xl border border-border py-3 z-[60]" data-testid="user-dropdown">
                        <div className="px-5 py-3 border-b border-border">
                          <p className="font-semibold text-foreground truncate">{user.business_name || user.name}</p>
                          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                          {user.is_verified_business && (
                            <span className="verified-badge mt-2 text-xs">✓ {t('verified_business')}</span>
                          )}
                        </div>
                        <div className="py-2">
                          <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2">
                            <User className="w-5 h-5 text-muted-foreground" />
                            {t('profile')}
                          </Link>
                          <Link to="/my-listings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2">
                            <Package className="w-5 h-5 text-muted-foreground" />
                            {t('my_listings')}
                          </Link>
                          <Link to="/my-events" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2">
                            <Calendar className="w-5 h-5 text-muted-foreground" />
                            My Events
                            {userEventCount > 0 && (
                              <span className="ml-auto px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">{userEventCount}</span>
                            )}
                          </Link>
                          <Link to="/my-bids" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2">
                            <Gavel className="w-5 h-5 text-muted-foreground" />
                            My Bids
                            {userBidCount > 0 && (
                              <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                                {userBidCount}
                              </span>
                            )}
                          </Link>
                          <Link to="/favorites" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2">
                            <Heart className="w-5 h-5 text-muted-foreground" />
                            Favorites
                          </Link>
                          <Link to="/trade-proposals" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2">
                            <ArrowLeftRight className="w-5 h-5 text-muted-foreground" />
                            Trade Proposals
                          </Link>
                          {isAdmin && (
                            <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2">
                              <Shield className="w-5 h-5 text-muted-foreground" />
                              Admin Panel
                            </Link>
                          )}
                        </div>
                        
                        {/* Settings Section */}
                        <div className="border-t border-border pt-3 mt-1 px-3">
                          <div className="flex items-center justify-between px-2 py-2">
                            <span className="text-sm text-foreground">Theme</span>
                            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                              {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
                            </button>
                          </div>
                          
                          <div className="px-2 py-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Language</span>
                            <div className="flex gap-1.5 mt-2">
                              {[{ code: 'en', label: 'EN', flag: '🇺🇸' }, { code: 'nl', label: 'NL', flag: '🇳🇱' }, { code: 'es', label: 'ES', flag: '🇪🇸' }].map((lang) => (
                                <button key={lang.code} onClick={() => setLanguage(lang.code)} className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${language === lang.code ? 'bg-primary text-white' : 'bg-secondary hover:bg-muted text-foreground'}`}>
                                  <span>{lang.flag}</span><span>{lang.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          <div className="px-2 py-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Currency</span>
                            <div className="flex gap-1.5 mt-2">
                              {[{ value: 'USD', label: '$ USD' }, { value: 'XCG', label: 'XCG' }].map((opt) => (
                                <button key={opt.value} onClick={() => setCurrency(opt.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${currency === opt.value ? 'bg-primary text-white' : 'bg-secondary hover:bg-muted text-foreground'}`}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          <div className="px-2 py-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Island</span>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {ISLAND_OPTIONS.map((island) => (
                                <button key={island.code} onClick={() => setCurrentIsland(island.code)} className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${currentIsland === island.code ? 'bg-primary text-white' : 'bg-secondary hover:bg-muted text-foreground'}`}>
                                  <span>{island.flag}</span><span>{island.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="border-t border-border pt-2 mt-1">
                          <button onClick={handleLogout} className="flex items-center gap-3 w-full px-5 py-3 text-red-500 hover:bg-red-500/10 transition-colors rounded-xl mx-2" style={{ width: 'calc(100% - 1rem)' }}>
                            <LogOut className="w-5 h-5" />
                            {t('sign_out')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Guest User */
                <div className="relative" ref={guestMenuRef}>
                  <button
                    {...safeClick(() => setGuestMenuOpen(!guestMenuOpen))}
                    className="flex items-center gap-2.5 p-1.5 rounded-full hover:bg-secondary transition-all duration-200 cursor-pointer touch-manipulation"
                    data-testid="guest-menu-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                      <UserCircle className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${guestMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {guestMenuOpen && (
                    <div className="absolute right-0 mt-3 w-64 bg-card rounded-2xl shadow-2xl border border-border py-3 z-[60]" data-testid="guest-dropdown">
                      <div className="px-4 pb-3 space-y-2">
                        <Link to="/auth" onClick={() => setGuestMenuOpen(false)} className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors">
                          {t('sign_in')}
                        </Link>
                        <Link to="/register" onClick={() => setGuestMenuOpen(false)} className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-secondary transition-colors">
                          {t('get_started')}
                        </Link>
                      </div>
                      
                      <div className="border-t border-border pt-3 px-3">
                        <div className="flex items-center justify-between px-2 py-2">
                          <span className="text-sm text-foreground">Theme</span>
                          <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                            {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
                          </button>
                        </div>
                        
                        <div className="px-2 py-2">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Language</span>
                          <div className="flex gap-1.5 mt-2">
                            {[{ code: 'en', label: 'EN', flag: '🇺🇸' }, { code: 'nl', label: 'NL', flag: '🇳🇱' }, { code: 'es', label: 'ES', flag: '🇪🇸' }].map((lang) => (
                              <button key={lang.code} onClick={() => setLanguage(lang.code)} className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${language === lang.code ? 'bg-primary text-white' : 'bg-secondary hover:bg-muted text-foreground'}`}>
                                <span>{lang.flag}</span><span>{lang.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div className="px-2 py-2">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Currency</span>
                          <div className="flex gap-1.5 mt-2">
                            {[{ value: 'USD', label: '$ USD' }, { value: 'XCG', label: 'XCG' }].map((opt) => (
                              <button key={opt.value} onClick={() => setCurrency(opt.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${currency === opt.value ? 'bg-primary text-white' : 'bg-secondary hover:bg-muted text-foreground'}`}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div className="px-2 py-2">
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Island</span>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {ISLAND_OPTIONS.map((island) => (
                              <button key={island.code} onClick={() => setCurrentIsland(island.code)} className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${currentIsland === island.code ? 'bg-primary text-white' : 'bg-secondary hover:bg-muted text-foreground'}`}>
                                <span>{island.flag}</span><span>{island.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Row - Search Bar */}
      <div className="bg-muted/50 py-2.5 relative z-[60]" style={{ overflow: 'visible' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <SearchBar height="h-10" />
        </div>
      </div>
    </nav>
  );
};

export default NavbarDesktop;
